import { DisputeReason, LanguageCode } from '#graphql-operations'
import { toTypedSchema } from '@vee-validate/zod'
import { z } from 'zod'

export const MAX_BODY_LENGTH = 2000
export const SUMMARY_MIN_LENGTH = 1
export const SUMMARY_MAX_LENGTH = 128
export const BODY_MIN_LENGTH = 1
export const BODY_MAX_LENGTH = 5000

export function useShippingAddressSchema() {
  const { t } = useI18n()
  return toTypedSchema(z.object({
    city: z.string({ message: t('form.field.required') }).min(1, { message: t('form.field.required', [1]) }).trim().default(''),
    company: z.string().trim().optional().default(''),
    countryCode: z.string({ message: t('form.field.required') }).min(2, { message: t('form.field.required', [2]) }).max(2, { message: t('form.field.required', [2]) }).trim().toUpperCase(),
    defaultBillingAddress: z.boolean().default(false),
    defaultShippingAddress: z.boolean().default(true),
    fullName: z.string({ message: t('form.field.required') }).min(1, { message: t('form.field.min_length', [1]) }).trim().default(''),
    phoneNumber: z.string({ message: t('form.field.required') }).min(1, { message: t('form.field.min_length', [1]) }).trim().default(''),
    postalCode: z.string({ message: t('form.field.required') }).min(1, { message: t('form.field.min_length', [1]) }).trim().default(''),
    province: z.string().trim().optional().default(''),
    streetLine1: z.string({ message: t('form.field.required') })
      .min(1, { message: t('form.field.required', [1]) })
      .regex(/\d/, { message: t('form.field.address_must_contain_number') })
      .trim()
      .default(''),
    streetLine2: z.string().trim().optional().default(''),
  }))
}

export function useBillingAddressSchema() {
  const { t } = useI18n()
  return toTypedSchema(z.object({
    city: z.string({ message: t('form.field.required') }).min(1, { message: t('form.field.required', [1]) }).trim().default(''),
    company: z.string().trim().optional().default(''),
    countryCode: z.string({ message: t('form.field.required') }).min(2, { message: t('form.field.required', [2]) }).max(2, { message: t('form.field.required', [2]) }).trim().toUpperCase(),
    defaultBillingAddress: z.boolean().default(false),
    defaultShippingAddress: z.boolean().default(false),
    fullName: z.string({ message: t('form.field.required') }).min(1, { message: t('form.field.min_length', [1]) }).trim(),
    phoneNumber: z.string({ message: t('form.field.required') }).min(1, { message: t('form.field.min_length', [1]) }).trim(),
    postalCode: z.string({ message: t('form.field.required') }).min(1, { message: t('form.field.min_length', [1]) }).trim(),
    province: z.string().trim().optional().default(''),
    streetLine1: z.string({ message: t('form.field.required') })
      .min(1, { message: t('form.field.min_length', [1]) })
      .regex(/\d/, { message: t('form.field.address_must_contain_number') })
      .trim(),
    streetLine2: z.string().trim().optional().default(''),
    customFields: z.object({
      companyId: z.string().trim().optional().default(''),
      vatId: z.string().trim().optional().default(''),
    }).default({ companyId: '', vatId: '' }),
  }))
}

export function useCustomerAddressSchema() {
  const { t } = useI18n()
  return toTypedSchema(z.object({
    city: z.string({ message: t('form.field.required') }).min(1, { message: t('form.field.required', [1]) }).trim(),
    company: z.string().trim().optional().default(''),
    countryCode: z.string({ message: t('form.field.required') }).min(2, { message: t('form.field.required', [2]) }).trim(),
    defaultBillingAddress: z.boolean().default(false),
    defaultShippingAddress: z.boolean().default(false),
    fullName: z.string({ message: t('form.field.required') }).min(1, { message: t('form.field.min_length', [1]) }).trim(),
    phoneNumber: z.string({ message: t('form.field.required') }).min(1, { message: t('form.field.min_length', [1]) }).trim(),
    postalCode: z.string({ message: t('form.field.required') }).min(1, { message: t('form.field.min_length', [1]) }).trim(),
    province: z.string().trim().optional().default(''),
    streetLine1: z.string({ message: t('form.field.required') })
      .min(1, { message: t('form.field.required', [1]) })
      .regex(/\d/, { message: t('form.field.address_must_contain_number') })
      .trim(),
    streetLine2: z.string().trim().optional().default(''),
  }))
}

export function useUpdateCustomerSchema() {
  const { t } = useI18n()
  return toTypedSchema(z.object({
    title: z.string({ message: t('form.field.required') }).trim().default(''),
    firstName: z.string({ message: t('form.field.required') }).min(1, { message: t('form.field.min_length', [1]) }).trim(),
    lastName: z.string({ message: t('form.field.required') }).min(1, { message: t('form.field.min_length', [1]) }).trim(),
    phoneNumber: z.string({ message: t('form.field.required') }).trim().default(''),
  }))
}

export function useRequestUpdateCustomerEmailAddressSchema() {
  const { t } = useI18n()
  return toTypedSchema(z.object({
    emailAddress: z.string({ message: t('form.field.required') }).email(t('form.email.invalid')).trim(),
    password: z.string({ message: t('form.field.required') }).min(4, { message: t('form.field.min_length', [4]) }).trim(),
  }))
}

export function useCouponCodeSchema() {
  const { t } = useI18n()
  return toTypedSchema(z.object({
    couponCode: z.string({ message: t('form.field.required') }).toUpperCase(),
  }))
}

export function useCreateCustomerSchema() {
  const { t } = useI18n()
  return toTypedSchema(z.object({
    emailAddress: z.string({ message: t('form.field.required') }).email(t('form.email.invalid')).trim(),
    firstName: z.string({ message: t('form.field.required') }).min(1, { message: t('form.field.min_length', [1]) }).trim(),
    lastName: z.string({ message: t('form.field.required') }).min(1, { message: t('form.field.min_length', [1]) }).trim(),
    phoneNumber: z.string({ message: t('form.field.required') }).min(1, { message: t('form.field.min_length', [1]) }).trim().default(''),
    title: z.string({ message: t('form.field.required') }).trim(),
  }))
}

export function useResetPasswordSchema() {
  const { t } = useI18n()
  return toTypedSchema(z.object({
    emailAddress: z.string({ message: t('form.field.required') }).email(t('form.email.invalid')).trim(),
  }))
}

export function useLoginSchema() {
  const { t } = useI18n()
  return toTypedSchema(z.object({
    emailAddress: z.string({ message: t('form.field.required') }).email(t('form.email.invalid')).trim(),
    password: z.string({ message: t('form.field.required') }).min(4, { message: t('form.field.min_length', [4]) }).trim(), // https://docs.vendure.io/reference/typescript-api/auth/auth-options#passwordvalidationstrategy
    rememberMe: z.boolean().default(true).optional(),
  }))
}

export function useProductReviewSchema() {
  const { t } = useI18n()
  return toTypedSchema(z.object({
    assetIds: z.array(
      z.string()
        .min(1, { message: t('form.field.min_length', [1]) })
        .refine(val => !Number.isNaN(Number(val)), {
          message: 'String must be a valid numeric value',
        }),
    ).optional().default([]),
    authorLocation: z.string()
      .min(2, { message: t('form.field.min_length', [2]) })
      .max(2, { message: t('form.field.max_length', [2]) })
      .regex(/^[A-Z]{2}$/, { message: t('form.field.country_code_invalid') })
      .trim()
      .toUpperCase(),
    authorName: z.string({ message: t('form.field.required') }).min(1, { message: t('form.field.min_length', [1]) }).trim(),
    body: z.string({ message: t('form.field.required') })
      .max(BODY_MAX_LENGTH, { message: t('form.field.max_length', [BODY_MAX_LENGTH]) })
      .trim()
      .default('')
      .optional(),
    customerId: z.string({ message: t('form.field.required') })
      .min(1, { message: t('form.field.min_length', [1]) })
      .trim()
      .optional(),
    languageCode: z.nativeEnum(LanguageCode),
    productId: z.string({ message: t('form.field.required') })
      .min(1, { message: t('form.field.min_length', [1]) })
      .trim(),
    rating: z.number({ message: t('form.field.required') })
      .int()
      .min(1, { message: t('form.field.required') })
      .max(5, { message: t('form.field.required') }),
    summary: z.string({ message: t('form.field.required') })
      .min(SUMMARY_MIN_LENGTH, { message: t('form.field.min_length', [SUMMARY_MIN_LENGTH]) })
      .max(SUMMARY_MAX_LENGTH, { message: t('form.field.max_length', [SUMMARY_MAX_LENGTH]) })
      .trim(),
    variantId: z.string({ message: t('form.field.required') })
      .min(1, { message: t('form.field.min_length', [1]) })
      .trim()
      .optional(),
  }))
}

export function useRegisterSchema() {
  const { t } = useI18n()
  return toTypedSchema(z.object({
    emailAddress: z.string({ message: t('form.field.required') }).email(t('form.email.invalid')).trim(),
    firstName: z.string({ message: t('form.field.required') }).trim().default(''),
    lastName: z.string({ message: t('form.field.required') }).trim().default(''),
    password: z.string({ message: t('form.field.required') }).min(4, { message: t('form.field.min_length', [4]) }).trim(),
    title: z.string({ message: t('form.field.required') }).trim().optional(),
    phoneNumber: z.string({ message: t('form.field.required') }).trim().optional(),
  }))
}

export function useUpdateCustomerPasswordSchema() {
  const { t } = useI18n()
  return toTypedSchema(z.object({
    currentPassword: z.string({ message: t('form.field.required') }).min(4, { message: t('form.field.min_length', [4]) }).trim(),
    newPassword: z.string({ message: t('form.field.required') }).min(4, { message: t('form.field.min_length', [4]) }).trim(),
  }).refine(data => data.currentPassword !== data.newPassword, {
    message: t('form.new_password.same_as_current'),
    path: ['newPassword'],
  }))
}

export function useRestockReminderSchema() {
  const { t } = useI18n()
  return toTypedSchema(z.object({
    email: z.string({ message: t('form.field.required') }).email({ message: t('form.email.invalid') }),
  }))
}

export function useVerifyEmailSchema() {
  const { t } = useI18n()
  return toTypedSchema(z.object({
    token: z.string({ message: t('form.field.required') }).min(1, { message: t('form.field.required', [1]) }).trim(),
    password: z.string({ message: t('form.field.required') }).min(4, { message: t('form.field.min_length', [4]) }).trim(),
  }))
}

export function useCreateDisputeSchema() {
  const { t } = useI18n()
  return toTypedSchema(z.object({
    assetIds: z.array(z.string({ message: t('form.field.required') }).min(1, { message: t('form.field.required', [1]) }).trim()).optional().default([]),
    comment: z.string({ message: t('form.field.required') }).min(10, { message: t('form.field.min_length', [10]) }).trim(),
    orderLineId: z.string({ message: t('form.field.required') }).min(1, { message: t('form.field.required', [1]) }).trim(),
    orderLineQuantity: z.number({ message: t('form.field.required') }).min(1, { message: t('form.field.required', [1]) }),
    reason: z.nativeEnum(DisputeReason),
    refundResolution: z.object({
      adjustment: z.string({ message: t('form.field.required') }).min(1, { message: t('form.field.required', [1]) }).trim(),
      quantity: z.number({ message: t('form.field.required') }).min(1, { message: t('form.field.required', [1]) }),
    }),
  }))
}

export function useAddDisputeProposalSchema() {
  const { t } = useI18n()
  return toTypedSchema(z.object({
    assetIds: z.array(z.string({ message: t('form.field.required') }).min(1, { message: t('form.field.required', [1]) }).trim()).optional().default([]),
    comment: z.string({ message: t('form.field.required') }).min(10, { message: t('form.field.min_length', [10]) }).trim(),
    disputeId: z.string({ message: t('form.field.required') }).min(1, { message: t('form.field.required', [1]) }).trim(),
    refundResolution: z.object({
      adjustment: z.string({ message: t('form.field.required') }).min(1, { message: t('form.field.required', [1]) }).trim(),
      quantity: z.number({ message: t('form.field.required') }).min(1, { message: t('form.field.required', [1]) }),
    }),
  }))
}
